import { defineStore, acceptHMRUpdate } from 'pinia';

interface LeoUserContext {
    type: string;
    displayName?: string;
    llm?: {
        prompt: string;
        arguments?: Record<string, unknown>;
    };
}

/** Pages supply either a ready-to-send question or a short label paired with a fuller prompt. */
type LeoSuggestedQuestion = string | { label: string; prompt: string };

interface LeoPageContextState {
    context: LeoUserContext | null;
    suggestedQuestions: LeoSuggestedQuestion[];
    ready: boolean;
    owner: number | null;
}

export function getInitialState (): LeoPageContextState {
    return {
        context: null,
        suggestedQuestions: [],
        ready: false,
        owner: null,
    };
}

/**
 * Holds the Leo context for the page the user is currently on so the global top
 * nav can open a context-aware conversation. The top nav is a sibling of the
 * router view, so the provide/inject `leoContext` chain cannot reach it.
 */
export const useLeoPageContextStore = defineStore('leoPageContext', {
    state: getInitialState,
    getters: {
        /** The page publishes context but its entity data has not resolved yet. */
        isPageContextLoading: (state): boolean => Boolean(state.context) && !state.ready,
        /** The page's context has resolved and is safe to send to Leo. */
        isPageContextReady: (state): boolean => Boolean(state.context) && state.ready,
    },
    actions: {
        setPageContext ({ owner, context, suggestedQuestions = [], ready = false }: {
            owner: number;
            context: LeoUserContext | null;
            suggestedQuestions?: LeoSuggestedQuestion[];
            ready?: boolean;
        }) {
            this.owner = owner;
            this.context = context;
            this.suggestedQuestions = suggestedQuestions;
            this.ready = ready;
        },
        /**
         * Vue mounts the incoming publisher before unmounting the outgoing one, so a departing
         * publisher must not wipe context its replacement has already set. Publishers that stay
         * mounted should call `setPageContext` with a null context instead of releasing.
         */
        releasePageContext (owner: number) {
            if (this.owner !== null && this.owner !== owner) {
                return;
            }

            Object.assign(this, getInitialState());
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useLeoPageContextStore, module.hot));
}
